import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import {
  ChevronLeftIcon,
  EmailIcon,
  FamilyThreeIcon,
  InformationIcon,
  PrintIcon,
  StarIcon,
  UserTwoIcon
} from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../api/apis.config'
import { diffuseOtoIndividualEndOfStayAppreciations } from '../../api/end-of-journey-reviews/sharing/diffuse-oto-individual-end-of-stay-appreciations'
import { acknowledgeOtoIndividualEnrollmentRemoval } from '../../api/enrollments/acknowledge-oto-individual-enrollment-removal'
import {
  assignOtoIndividualHostFamily,
  changeOtoIndividualHostFamily,
  unassignOtoIndividualHostFamily
} from '../../api/enrollments/assign-oto-individual-host-family'
import { diffuseOtoIndividualHostFamiliesToParents } from '../../api/enrollments/diffuse-oto-individual-host-families-to-parents'
import { sendOtoIndividualEnrollmentsToHostFamily } from '../../api/enrollments/send-oto-individual-enrollments-to-host-family'
import { updateOtoIndividualTransferPerson } from '../../api/enrollments/update-oto-individual-transfer-person'
import { viewOtoIndividualEnrollment } from '../../api/enrollments/view-oto-individual-enrollment'
import { getPeriods } from '../../api/periods/get-periods'
import { getPeriodsPlacementInfo } from '../../api/periods/get-periods-placement-info'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../components/elements/information-tooltip'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import OlCenterAssignHostFamilyForm from '../../components/pages/ol-common/ol-center-placement-enrollment-table/ol-center-assign-host-family-form'
import OlCenterChangeHostFamilyForm from '../../components/pages/ol-common/ol-center-placement-enrollment-table/ol-center-change-host-family-form'
import OtoIndividualPlacementFilter from '../../components/pages/oto-individual/oto-individual-placement-filter'
import OtoIndividualTransferPersonForm, {
  toOtoIndividualTransferPersonInput
} from '../../components/pages/oto-individual/oto-individual-transfer-person-form'
import { getOtoIndividualPlacementColumns } from '../../components/pages/oto-individual/utils'
import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import {
  OtoIndividualPlacement,
  OtoIndividualPlacementFilters
} from '../../lib/types/models/enrollment/oto-individual-enrollments'
import { EnrollmentDiffusionStateEnum } from '../../lib/types/models/enrollment-center/enrollment-center'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformOtoIndividualPlacementFiltersToUrl,
  transformUrlToOtoIndividualPlacementFilters
} from '../../lib/utils/filters-mapping/oto-individual-placement'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

export function OtoIndividualPlacesListPage() {
  const { t } = useTranslate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { downloadDocuments } = useDownloadDocuments()
  const { navigate } = useNavigator()
  const tableRef = useRef<DynamicTableRef<OtoIndividualPlacement>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  // the youths the provider has not seen yet, highlighted once per load like on the centers
  const [newPlacements, setNewPlacements] = useState<number[]>([])
  const [acknowledgedPlacementId, setAcknowledgedPlacementId] = useState<number | null>(null)
  const firstLoadRef = useRef(false)

  const periodId = Number(id)
  // the list endpoint is built from the périod id, so an invalid one must never reach it
  const hasValidPeriodId = !!id && Number.isInteger(periodId) && periodId > 0
  const olId = user.ol?.id
  const isOl = user.role === RoleEnum.Ol

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'assign-host-family' | 'change-host-family' | 'transfer-person' | 'filters',
    OtoIndividualPlacement
  >(['assign-host-family', 'change-host-family', 'transfer-person', 'filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<OtoIndividualPlacementFilters>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOtoIndividualPlacementFiltersToUrl,
      transformUrlToFilter: transformUrlToOtoIndividualPlacementFilters
    })

  const {
    data: period,
    isPending: isPeriodPending,
    isError: isPeriodError
  } = useQuery({
    queryKey: ['oto-individual-period', periodId, 'OTO_INDIVIDUAL'],
    queryFn: () => getPeriods({ filter: { periodId }, version: 'OTO_INDIVIDUAL' }),
    select: (data) => data.data?.[0] ?? null,
    enabled: hasValidPeriodId
  })

  const {
    data: placementInfo,
    isFetching: isPlacementInfoLoading,
    refetch: refreshPeriodPlacementInfo
  } = useQuery({
    queryKey: ['periods-placement-info', 'periodId', period?.id],
    queryFn: () => getPeriodsPlacementInfo({ periodId: period?.id }),
    enabled: !!period?.id
  })

  const assignHostFamilyMutation = useMutation({
    mutationKey: ['assign-oto-individual-host-family'],
    mutationFn: assignOtoIndividualHostFamily,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      refreshPeriodPlacementInfo()
      toast.success(t('oto.individual.placements.host.family.assigned'))
    }
  })

  const changeHostFamilyMutation = useMutation({
    mutationKey: ['change-oto-individual-host-family'],
    mutationFn: changeOtoIndividualHostFamily,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      refreshPeriodPlacementInfo()
      toast.success(t('oto.individual.placements.host.family.changed'))
    }
  })

  const unassignHostFamilyMutation = useMutation({
    mutationKey: ['unassign-oto-individual-host-family'],
    mutationFn: unassignOtoIndividualHostFamily,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      refreshPeriodPlacementInfo()
      toast.success(t('oto.individual.placements.host.family.unassigned'))
    }
  })

  const diffuseHostFamiliesToParentsMutation = useMutation({
    mutationKey: ['diffuse-oto-individual-host-families-to-parents'],
    mutationFn: diffuseOtoIndividualHostFamiliesToParents,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success(t('oto.individual.placements.info.sent.success'))
    }
  })

  // "Diffusion des appréciations de fin de séjour" of OTO individuel youths (no centre): the provider
  // sees their host family ratings
  const diffuseEndOfStayAppreciationsMutation = useMutation({
    mutationKey: ['diffuse-oto-individual-end-of-stay-appreciations'],
    mutationFn: diffuseOtoIndividualEndOfStayAppreciations,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success(t('oto.individual.placements.end.of.stay.appreciations.diffused.success'))
    }
  })

  const sendEnrollmentsToHostFamilyMutation = useMutation({
    mutationKey: ['send-oto-individual-enrollments-to-host-family'],
    mutationFn: sendOtoIndividualEnrollmentsToHostFamily,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success(t('oto.individual.placements.enrollments.info.sent.success'))
    }
  })

  const updateTransferPersonMutation = useMutation({
    mutationKey: ['update-oto-individual-transfer-person'],
    mutationFn: updateOtoIndividualTransferPerson,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success(t('oto.individual.placements.transfer.person.updated.success'))
    }
  })

  const acknowledgeRemovalMutation = useMutation({
    mutationKey: ['acknowledge-oto-individual-enrollment-removal'],
    mutationFn: acknowledgeOtoIndividualEnrollmentRemoval,
    onError: (error) => {
      setAcknowledgedPlacementId(null)
      showAxiosError(error)
    },
    onSuccess: () => {
      setAcknowledgedPlacementId(null)
      tableRef.current?.refetch()
      refreshPeriodPlacementInfo()
      toast.success(t('oto.individual.placements.removal.acknowledged'))
    }
  })

  const viewPlacementMutation = useMutation({
    mutationKey: ['view-oto-individual-enrollment'],
    mutationFn: viewOtoIndividualEnrollment,
    onError: showAxiosError
  })

  useEffect(() => {
    for (const enrollmentId of newPlacements) viewPlacementMutation.mutate({ enrollmentId })
  }, [newPlacements])

  const columns = getOtoIndividualPlacementColumns(
    t,
    user.role,
    isOl
      ? (placement) =>
          toggleModal(
            placement.otoIndividualProposition.hostFamily
              ? 'change-host-family'
              : 'assign-host-family',
            placement
          )
      : undefined,
    // the provider records the person it sends to the airport, LEC only reads the state
    isOl ? (placement) => toggleModal('transfer-person', placement) : undefined,
    // only the provider acknowledges a removal from its own list
    isOl
      ? {
          onClick: (placement) => {
            setAcknowledgedPlacementId(placement.id)
            acknowledgeRemovalMutation.mutate({ enrollmentId: placement.id })
          },
          isPending: acknowledgeRemovalMutation.isPending,
          pendingId: acknowledgedPlacementId
        }
      : undefined
  )

  if (!hasValidPeriodId)
    return <ItemNotFound title={t('oto.individual.placements.period.invalid')} />
  if (isPeriodPending) return <LoadingScreen />
  if (isPeriodError || !period)
    return <ItemNotFound title={t('oto.individual.placements.period.not.found')} />

  return (
    <div>
      <UserNavBar
        title={t('oto.individual.placements.title')}
        icon={ChevronLeftIcon}
        onIconClick={() => navigate(Paths.OTO_INDIVIDUAL_PLACE_PERIODS_LIST_PAGE)}
      />
      <div className={'h-4'} />

      <Box>
        <div className="flex flex-row items-center gap-4 bg-white-hover border border-grey-light w-fit p-2 rounded">
          <Typography variant="caption">{t('oto.individual.placements.period.label')}</Typography>
          <PeriodBadge period={period} />
        </div>

        <DynamicDataTable<OtoIndividualPlacement, OtoIndividualPlacementFilters>
          ref={tableRef}
          className="p-0"
          onLoadingChange={setIsTableLoading}
          title={t('oto.individual.placements.table.title')}
          columns={columns}
          selectable
          allowSearchParam
          label={() => (
            <div className="flex flex-row gap-2.5 items-center">
              <Badge
                variant="orange"
                loading={isPlacementInfoLoading}
                text={t('oto.individual.periods.placement.info', {
                  total: placementInfo?.total ?? 0,
                  placed: placementInfo?.placed ?? 0,
                  unplaced: placementInfo?.unplaced ?? 0
                })}
              />

              {isOl && period && !period.viewData?.olAllHostFamilyInfoSent && (
                <InformationToolTip
                  contentVariant="secondary"
                  icon={
                    <Badge
                      variant="secondaryRed"
                      icon={<InformationIcon color={tailwindColors.secondaryRed.DEFAULT} />}
                    />
                  }
                >
                  <Typography
                    variant="label-medium"
                    color={tailwindColors.black.DEFAULT}
                    className="text-pretty"
                  >
                    {t('oto.individual.placements.info.not.fully.sent.to.parents')}
                  </Typography>
                </InformationToolTip>
              )}
            </div>
          )}
          endpoint={constructApiUrl(API.enrollments.getOtoIndividualEnrollmentsOfPeriod.url, {
            periodId
          })}
          specialRows={[
            {
              color: tailwindColors.green.light.DEFAULT,
              condition: ({ id }) => newPlacements.includes(id)
            },
            {
              color: tailwindColors.red.light.DEFAULT,
              condition: ({ otoIndividualProposition }) =>
                otoIndividualProposition.diffusionState === EnrollmentDiffusionStateEnum.DELETED
            }
          ]}
          onDataChange={(data) => {
            if (!isOl || firstLoadRef.current) return
            firstLoadRef.current = true

            setNewPlacements(
              data
                .filter(
                  ({ otoIndividualProposition }) =>
                    otoIndividualProposition.diffusionState ===
                      EnrollmentDiffusionStateEnum.EXISTING && !otoIndividualProposition.viewedByOl
                )
                .map(({ id }) => id)
            )
          }}
          permissions={{ delete: false }}
          filters={selectedFilter}
          filtersLength={countFilters(selectedFilter)}
          onFiltersClick={() => toggleModal('filters')}
          actions={[
            user.role === RoleEnum.ADMIN
              ? {
                  type: 'multiple',
                  variant: 'primary',
                  label: t('oto.individual.placements.actions.diffuse.info.to.parents'),
                  refetch: true,
                  fn: (rows) =>
                    diffuseHostFamiliesToParentsMutation.mutateAsync({
                      ids: rows.map((placement) => placement.id)
                    })
                }
              : {
                  type: 'multiple',
                  variant: 'primary',
                  icon: EmailIcon,
                  label: t('oto.individual.placements.actions.send.host.family.info'),
                  refetch: true,
                  fn: (rows) =>
                    sendEnrollmentsToHostFamilyMutation.mutateAsync({
                      ids: rows.map((placement) => placement.id)
                    })
                },
            {
              type: 'multiple',
              variant: 'ghost',
              icon: StarIcon,
              label: t('oto.individual.placements.actions.diffuse.end.of.stay.appreciations'),
              hasAccess: user.role === RoleEnum.ADMIN,
              refetch: true,
              fn: (rows) =>
                diffuseEndOfStayAppreciationsMutation.mutateAsync({
                  ids: rows.map((placement) => placement.id),
                  diffused: true
                })
            },
            {
              type: 'global',
              variant: 'ghost',
              icon: PrintIcon,
              label: t('oto.individual.placements.actions.print.transfers.list'),
              fn: () =>
                downloadDocuments({
                  endpoint: constructApiUrl(
                    API.enrollments.generateOtoIndividualStudentsAndTransfersPdf.url,
                    { periodId }
                  )
                })
            }
          ]}
        />
      </Box>

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <OtoIndividualPlacementFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {olId && selectedData && (
        <>
          <Modal
            isOpen={modalState['assign-host-family']}
            title={t('oto.individual.placements.host.family.assign')}
            icon={FamilyThreeIcon}
            size="medium"
            hideActions
            onCancel={() => closeModel('assign-host-family')}
          >
            <OlCenterAssignHostFamilyForm
              olId={olId}
              className={MODAL_CONTENT_CLASSNAME}
              buttonsConfig={modalButtonConfig({
                onCancel() {
                  closeModel('assign-host-family')
                },
                confirmText: t('button.assign'),
                cancelText: t('button.close'),
                onConfirm: async (data) => {
                  await assignHostFamilyMutation.mutateAsync({
                    ...data,
                    enrollmentId: selectedData.id
                  })
                  closeModel('assign-host-family')
                }
              })}
            />
          </Modal>

          <Modal
            isOpen={modalState['change-host-family']}
            title={t('oto.individual.placements.host.family.change')}
            icon={FamilyThreeIcon}
            size="medium"
            hideActions
            onCancel={() => closeModel('change-host-family')}
          >
            <OlCenterChangeHostFamilyForm
              olId={olId}
              className={MODAL_CONTENT_CLASSNAME}
              buttonsConfig={modalButtonConfig({
                onCancel() {
                  closeModel('change-host-family')
                },
                confirmText: t('button.assign'),
                cancelText: t('button.close'),
                onConfirm: async (data) => {
                  if (!data.hostFamilyId)
                    await unassignHostFamilyMutation.mutateAsync({
                      enrollmentId: selectedData.id,
                      reasonForChange: data.reasonForChange
                    })
                  else
                    await changeHostFamilyMutation.mutateAsync({
                      ...data,
                      enrollmentId: selectedData.id
                    })
                  closeModel('change-host-family')
                }
              })}
            />
          </Modal>

          <Modal
            isOpen={modalState['transfer-person']}
            title={t('oto.individual.placements.transfer.person.title')}
            icon={UserTwoIcon}
            size="medium"
            hideActions
            onCancel={() => closeModel('transfer-person')}
          >
            <OtoIndividualTransferPersonForm
              className={MODAL_CONTENT_CLASSNAME}
              defaultValues={toOtoIndividualTransferPersonInput(
                selectedData.otoIndividualProposition.transferPerson
              )}
              buttonsConfig={modalButtonConfig({
                onCancel() {
                  closeModel('transfer-person')
                },
                confirmText: t('button.validate'),
                cancelText: t('button.close'),
                onConfirm: async (data) => {
                  await updateTransferPersonMutation.mutateAsync({
                    ...data,
                    enrollmentId: selectedData.id
                  })
                  closeModel('transfer-person')
                }
              })}
            />
          </Modal>
        </>
      )}
    </div>
  )
}
